import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { categories, listingsByCategory } from "@/lib/data";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/category/")({
  head: () => {
    const title = "Browse Businesses by Category | LetsListBusiness";
    const description = "Find the best local restaurants, coffee shops, home services, and more. Explore all business categories on LetsListBusiness.";
    const url = SITE_URL + "/category/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Business Categories" },
        { property: "og:description", content: "Explore businesses by category." },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Categories", href: "/category/" },
        ], SITE_URL),
      ],
    };
  },
  component: CategoriesIndex,
});

function CategoriesIndex() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Categories" }]} />

      {/* Hero — more immersive */}
      <section className="container mx-auto px-4 pb-16 pt-8 text-center">
        <div className="mx-auto max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-[0.4em] text-primary">Discover your niche</div>
          <h1 className="mt-6 font-display text-6xl font-bold leading-[1.05] sm:text-8xl">
            Choose your <em className="not-italic text-primary">curiosity.</em>
          </h1>
          <p className="mt-8 text-xl leading-relaxed text-muted-foreground">
            From the best brisket in Texas to the most reliable plumbers in Brooklyn, we've organized the city so you don't have to. Browse by category to find exactly what you need.
          </p>
        </div>
      </section>

      {/* The Mosaic — Unique Asymmetrical Grid */}
      <section className="container mx-auto px-4 pb-24">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-12">
          {categories.map((c, i) => {
            const count = listingsByCategory(c.slug).length;
            
            // Define unique spans for an asymmetrical "Editorial" look
            const spans = [
              "sm:col-span-8 sm:h-[500px]", // 1: Hero
              "sm:col-span-4 sm:h-[500px]", // 2: Tall
              "sm:col-span-4 sm:h-[400px]", // 3: Square
              "sm:col-span-4 sm:h-[400px]", // 4: Square
              "sm:col-span-4 sm:h-[400px]", // 5: Square
              "sm:col-span-6 sm:h-[450px]", // 6: Wide
              "sm:col-span-6 sm:h-[450px]", // 7: Wide
              "sm:col-span-12 sm:h-[350px]", // 8: Banner
            ];

            return (
              <Link
                key={c.slug}
                to="/category/$slug"
                params={{ slug: c.slug }}
                className={`group relative flex flex-col overflow-hidden rounded-4xl bg-card shadow-card transition-all hover:-translate-y-1 hover:shadow-card-lg ${spans[i % spans.length]}`}
              >
                <img 
                  src={c.image} 
                  alt={c.name} 
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" 
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/20 to-transparent" />
                
                <div className="relative mt-auto p-8 text-white">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl drop-shadow-lg">{c.icon}</span>
                    <div className="h-px flex-1 bg-white/20" />
                    <span className="text-xs font-mono opacity-80">{count} listings</span>
                  </div>
                  <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">{c.name}</h2>
                  <p className="mt-3 max-w-sm text-sm text-white/70 line-clamp-2 transition-all group-hover:line-clamp-none">
                    {c.description}
                  </p>
                  
                  <div className="mt-6 flex items-center gap-2 overflow-hidden opacity-0 transition-all group-hover:opacity-100">
                    <span className="text-sm font-bold">Explore category</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>

                {/* Decorative index number */}
                <div className="absolute right-8 top-8 font-mono text-sm text-white/30">
                  0{i + 1}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Curate Section — content expansion */}
      <section className="bg-surface py-20">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="font-display text-4xl font-bold">How we curate our <em className="not-italic text-primary">local favorites.</em></h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                We don't just scrape the web for data. Every category you see here is a result of hundreds of hours of local research. We look for businesses that have stood the test of time, offer exceptional value, and contribute to the unique flavor of their neighborhoods.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Verified physical addresses and business hours.",
                  "Consistent high ratings across multiple platforms.",
                  "Community-driven feedback and local recommendations.",
                  "Independent ownership and unique service offerings."
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary">{i + 1}</span>
                    <span className="text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4 pt-12">
                <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=80" alt="Local business" className="rounded-2xl" />
                <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&q=80" alt="Local service" className="rounded-2xl" />
              </div>
              <div className="space-y-4">
                <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&q=80" alt="Local restaurant" className="rounded-2xl" />
                <img src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&q=80" alt="Local beauty" className="rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article — Internal Linking */}
      <section className="container mx-auto px-4 py-20">
        <div className="rounded-[3rem] border border-border bg-card p-1 items-center grid lg:grid-cols-2">
           <div className="p-8 sm:p-12 lg:p-16">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">From the blog</div>
              <h2 className="mt-6 font-display text-4xl font-bold leading-tight">Finding the best local businesses: A guide to our process.</h2>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Want to know exactly how we vet our listings? Our editorial team breaks down the metrics we use to ensure every business on LetsListBusiness is worth your time and money.
              </p>
              <Link 
                to="/blog/$slug" 
                params={{ slug: "local-business-guide-nashville" }} 
                className="mt-8 inline-flex items-center justify-center rounded-full bg-foreground px-8 py-4 text-sm font-bold text-background transition-transform hover:scale-105"
              >
                Read the full guide
              </Link>
           </div>
           <div className="hidden lg:block h-full">
              <img 
                src="https://images.unsplash.com/photo-1454165833767-027508496d4c?w=1200&q=80" 
                alt="Business analysis" 
                className="h-full w-full object-cover rounded-[2.8rem]"
              />
           </div>
        </div>
      </section>
    </>
  );
}
